import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  Paper,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import FaceRetouchingOffIcon from "@mui/icons-material/FaceRetouchingOff";
import { useEmployeeService } from "../../services/EmployeeService";

dayjs.extend(utc);

const COLORS = ["#28a745", "#dc3545"];

const EmployeeDetailedLog = ({ empID }) => {
  const { fetchDetailedLog } = useEmployeeService();
  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("TABLE"); // or 'CHART'

  const fetchLogs = async () => {
    if (!empID || startDate.isAfter(endDate)) return;
    setLoading(true);
    try {
      const data = await fetchDetailedLog({
        UserGUID: empID,
        startdate: startDate.format("YYYY-MM-DD"),
        enddate: endDate.format("YYYY-MM-DD"),
      });
      setLogs(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [empID]);

  const filteredLogs =
    filter === "ALL" ? logs : logs.filter((log) => log.ActionStatus === filter);
  const dataGridRows = filteredLogs.map((row, i) => ({ id: i, ...row }));

  const columns = [
    {
      field: "ActionDateTime",
      headerName: "Date & Time",
      flex: 1.5,
      valueGetter: (params) => params ?? null,
      valueFormatter: (params) => {
        const rawValue = params;
        if (!rawValue) return "N/A";
        const date = dayjs(rawValue);
        return date.isValid()
          //? date.local().format("DD-MMM-YY hh:mm A")
          ? dayjs(rawValue.replace(/Z$/, "")).format("DD-MMM-YY hh:mm A")

          : "N/A";
      },
    },
    { field: "Action", headerName: "Action", flex: 1 },
    { field: "Latitude", headerName: "Latitude", flex: 1 },
    { field: "Longitude", headerName: "Longitude", flex: 1 },
    {
      field: "ActionStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            background: params.value === "Success" ? "#d4edda" : "#f8d7da",
            color: params.value === "Success" ? "#155724" : "#721c24",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: "0.75rem",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "distanceFromAssignedLoc",
      headerName: "Distance From Assigned Loc",
      flex: 1.5,
      valueFormatter: (params) =>
        typeof params === "number" ? params.toFixed(2) : "0.00",
    },
    {
      field: "ActionRemarks",
      headerName: "Validation",
      flex: 2,
      renderCell: (params) => {
        const value = params.value || "";
        const showLocationSuccess = value.includes(
          "Location validation Succeeded"
        );
        const showLocationFail = value.includes("Location Validation Failed");
        const showFaceSuccess = value.includes("Face validation Succeeded");
        const showFaceFail = value.includes("Face validation Failed");

        return (
          <Box display="flex" gap={1}>
            {showLocationSuccess && (
              <Tooltip title="Location Validated">
                <LocationOnIcon color="success" fontSize="small" />
              </Tooltip>
            )}
            {showLocationFail && (
              <Tooltip title="Location Validation Failed">
                <LocationOffIcon color="error" fontSize="small" />
              </Tooltip>
            )}
            {showFaceSuccess && (
              <Tooltip title="Face Validated">
                <FaceRetouchingNaturalIcon color="success" fontSize="small" />
              </Tooltip>
            )}
            {showFaceFail && (
              <Tooltip title="Face Validation Failed">
                <FaceRetouchingOffIcon color="error" fontSize="small" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const summaryStats = useMemo(() => {
    const success = logs.filter((log) => log.ActionStatus === "Success");
    const failed = logs.filter((log) => log.ActionStatus === "Failed");

    const locationFails = failed.filter((log) =>
      log.ActionRemarks?.includes("Location Validation Failed")
    );
    const faceFails = failed.filter((log) =>
      log.ActionRemarks?.includes("Face validation Failed")
    );
    const bothFails = failed.filter(
      (log) =>
        log.ActionRemarks?.includes("Location Validation Failed") &&
        log.ActionRemarks?.includes("Face validation Failed")
    );

    const lineDataMap = {};

    logs.forEach((log) => {
      if (log.ActionStatus !== "Failed") return;
      const date = dayjs(log.ActionDateTime).format("YYYY-MM-DD");

      if (!lineDataMap[date]) {
        lineDataMap[date] = { date, faceFailures: 0, locationFailures: 0 };
      }

      if (log.ActionRemarks?.includes("Face validation Failed")) {
        lineDataMap[date].faceFailures += 1;
      }

      if (log.ActionRemarks?.includes("Location Validation Failed")) {
        lineDataMap[date].locationFailures += 1;
      }
    });

    const line = Object.values(lineDataMap).sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return {
      pie: [
        { name: "Success", value: success.length },
        { name: "Failed", value: failed.length },
      ],
      bar: [
        { name: "Location", count: locationFails.length },
        { name: "Face", count: faceFails.length },
        // { name: "Both", count: bothFails.length },
      ],
      line,
    };
  }, [logs]);

  return (
    <Box>
      {/* <Tabs value={tabIndex} onChange={(e, newVal) => setTabIndex(newVal)}>
        <Tab label="Activity Logs" />
        <Tab label="Validation Summary" />
      </Tabs> */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">
          {viewMode === "TABLE" ? "Activity Logs" : "Validation Summary"}{" "}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            format="DD-MMM-YYYY"
            onChange={(newVal) => setStartDate(newVal)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            format="DD-MMM-YYYY"
            onChange={(newVal) => setEndDate(newVal)}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button onClick={fetchLogs} variant="contained" size="small">
            Get
          </Button>
          {viewMode === "TABLE" && (
            <ToggleButtonGroup
              exclusive
              value={filter}
              onChange={(_, val) => val && setFilter(val)}
              size="small"
              sx={{ ml: 1 }}
            >
              <ToggleButton value="ALL">ALL</ToggleButton>
              <ToggleButton value="Success" sx={{ color: "green" }}>
                SUCCESS
              </ToggleButton>
              <ToggleButton value="Failed" sx={{ color: "red" }}>
                FAILED
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={(_, val) => val && setViewMode(val)}
            size="small"
          >
            <ToggleButton value="TABLE">Table</ToggleButton>
            <ToggleButton value="CHART">Chart</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {viewMode === "TABLE" && (
        <>
          <Box sx={{ height: "35vh", overflow: "auto" }}>
            <DataGrid
              rows={dataGridRows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              hideFooterPagination
              hideFooter
              density="compact"
            />
          </Box>
        </>
      )}

      {viewMode === "CHART" && (
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle1">Overall Status</Typography>
              <ResponsiveContainer width="100%" height={210}>
                <PieChart >
                  <Pie
                    data={summaryStats.pie}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={80}
                    label
                  >
                    {summaryStats.pie.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <ReTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle1">Failure Reasons</Typography>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={summaryStats.bar}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <ReTooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#f44336" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle1">Failure Trend (Daily)</Typography>
               <ResponsiveContainer width="100%" height={210}>
  <LineChart
                width="100%"
                height={270}
                data={summaryStats.line}
                margin={{ top: 5, right: 5, left: 2, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <ReTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="faceFailures"
                  stroke="#f08080"
                  name="Face Failures"
                />
                <Line
                  type="monotone"
                  dataKey="locationFailures"
                  stroke="#ffa500"
                  name="Location Failures"
                />
              </LineChart>
               </ResponsiveContainer>
            
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EmployeeDetailedLog;
